import React, { useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

// Edits the intro paragraph + header image shown on a group's auto overview page.
// `onApply(patch)` writes into the menu draft; the admin then saves the menu as usual.
export const GroupOverviewEditor = ({ node, open, onClose, onApply, applyLabel = 'Apply to menu draft', hint = true }) => {
  const { uploadImage, mediaUrl } = useAuth();
  const [intro, setIntro] = useState(node.intro || '');
  const [image, setImage] = useState(node.headerImage || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const apply = async () => {
    setBusy(true); setErr('');
    try { await onApply({ intro: intro.trim(), headerImage: image }); onClose(); }
    catch { setErr('Could not save. Are you signed in as admin?'); }
    setBusy(false);
  };

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setErr('');
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        const url = await uploadImage(reader.result, file.type || 'image/jpeg');
        setImage(url);
      } catch { setErr('Upload failed. Try a smaller image.'); }
      setBusy(false);
    };
    reader.onerror = () => setErr('Could not read that image.');
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="group-overview-editor">
        <DialogHeader>
          <DialogTitle className="font-title" style={{ color: 'var(--navy)' }}>Overview page — {node.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-title block mb-1" style={{ color: 'var(--olive)' }}>Introductory paragraph</label>
            <Textarea
              data-testid="group-overview-intro-input"
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={5}
              placeholder="A short welcome or explanation shown above the page cards. Separate paragraphs with a blank line."
            />
          </div>
          <div>
            <label className="text-sm font-title block mb-1" style={{ color: 'var(--olive)' }}>Header image</label>
            {image && (
              <div className="relative mb-2 rounded overflow-hidden border" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
                <img src={mediaUrl(image)} alt="" className="w-full h-36 object-cover" data-testid="group-overview-image-preview" />
                <button type="button" data-testid="group-overview-image-remove" onClick={() => setImage('')} title="Remove image" className="absolute top-2 right-2 p-1.5 rounded" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}><Trash2 size={14} /></button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm cursor-pointer" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Upload image
                <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={busy} data-testid="group-overview-image-file" />
              </label>
              <Input
                data-testid="group-overview-image-url"
                value={image}
                onChange={(e) => setImage(e.target.value.trim())}
                placeholder="…or paste an image URL"
                className="text-sm"
              />
            </div>
            {err && <p className="text-xs mt-1" style={{ color: 'var(--maroon)' }} data-testid="group-overview-editor-error">{err}</p>}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="group-overview-editor-cancel">Cancel</Button>
          <Button
            data-testid="group-overview-editor-apply"
            disabled={busy}
            onClick={apply}
            style={{ background: 'var(--navy)', color: 'var(--cream)' }}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : applyLabel}
          </Button>
        </DialogFooter>
        {hint && <p className="text-xs" style={{ color: 'var(--olive)' }}>Remember to click <strong>Save menu</strong> afterwards to publish these changes.</p>}
      </DialogContent>
    </Dialog>
  );
};
